import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription, catchError, of, switchMap, timer } from 'rxjs';
import { AccountService } from '../core/account.service';
import { AuthService } from '../core/auth.service';
import { assetLabel, formatCompact, formatMoney, formatPct, formatPrice, formatQty } from '../core/format';
import { errorMessage } from '../core/http-error';
import { MarketDetail, Side } from '../core/models';
import { LanguageService } from '../core/language';
import { MarketService } from '../core/market.service';
import { ToastService } from '../core/toast.service';
import { CandleChart } from '../ui/candle-chart';

@Component({
  selector: 'app-trade',
  imports: [CandleChart, RouterLink, FormsModule],
  templateUrl: './trade.html',
})
export class TradePage implements OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly markets = inject(MarketService);
  private readonly account = inject(AccountService);
  private readonly toast = inject(ToastService);
  protected readonly auth = inject(AuthService);
  protected readonly lang = inject(LanguageService);

  protected readonly detail = signal<MarketDetail | null>(null);
  protected readonly missing = signal(false);
  protected readonly side = signal<Side>('BUY');
  protected readonly quantity = signal(1);
  protected readonly submitting = signal(false);
  protected readonly watching = signal<string[]>([]);
  protected readonly price = formatPrice;
  protected readonly pct = formatPct;
  protected readonly money = formatMoney;
  protected readonly qty = formatQty;
  protected readonly compact = formatCompact;
  protected readonly label = assetLabel;

  private readonly subscription: Subscription;

  constructor() {
    this.subscription = this.route.paramMap
      .pipe(
        switchMap((params) => {
          const symbol = (params.get('symbol') ?? '').toUpperCase();
          this.detail.set(null);
          this.missing.set(false);
          this.quantity.set(symbol.includes('-') ? 0.1 : 1);
          return timer(0, 2000).pipe(
            switchMap(() =>
              this.markets.detail(symbol).pipe(
                catchError((error: HttpErrorResponse) => {
                  if (error.status === 404) {
                    this.missing.set(true);
                  }
                  return of(null);
                }),
              ),
            ),
          );
        }),
      )
      .subscribe((detail) => {
        if (detail) {
          this.detail.set(detail);
        }
      });

    if (localStorage.getItem('liiji.token')) {
      this.auth.refresh();
      this.account.load().subscribe({
        next: (book) => this.watching.set(book.watchlist),
        error: () => undefined,
      });
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  protected readonly quote = computed(() => this.detail()?.quote ?? null);

  protected readonly watched = computed(() => {
    const quote = this.quote();
    return quote ? this.watching().includes(quote.symbol) : false;
  });

  protected readonly fill = computed(() => {
    const quote = this.quote();
    if (!quote) {
      return 0;
    }
    const touch = this.side() === 'BUY' ? quote.price * 1.0002 : quote.price * 0.9998;
    const factor = 10 ** quote.precision;
    return Math.round(touch * factor) / factor;
  });

  protected readonly notional = computed(() => this.fill() * (Number(this.quantity()) || 0));

  protected readonly presets = computed(() => (this.quote()?.assetClass === 'CRYPTO' ? [0.01, 0.1, 0.5, 1] : [1, 5, 10, 25]));

  protected setQuantity(value: number): void {
    this.quantity.set(value);
  }

  protected onQuantity(value: number | string): void {
    const next = Number(value);
    this.quantity.set(Number.isFinite(next) ? next : 0);
  }

  protected toggleWatch(): void {
    const quote = this.quote();
    if (!quote) {
      return;
    }
    const request = this.watched() ? this.account.unwatch(quote.symbol) : this.account.watch(quote.symbol);
    request.subscribe({
      next: (watchlist) => this.watching.set(watchlist),
      error: (error: unknown) => this.toast.show(errorMessage(error, this.lang.t('trWatchFail')), 'bad'),
    });
  }

  protected submit(): void {
    const quote = this.quote();
    const quantity = Number(this.quantity());
    if (!quote || !Number.isFinite(quantity) || quantity <= 0) {
      this.toast.show(this.lang.t('trQtyZero'), 'bad');
      return;
    }
    if (!this.auth.signedIn()) {
      void this.router.navigate(['/signin'], { queryParams: { returnUrl: `/trade/${quote.symbol}` } });
      return;
    }
    this.submitting.set(true);
    this.account.place(quote.symbol, this.side(), quantity).subscribe({
      next: (order) => {
        this.submitting.set(false);
        this.auth.refresh();
        this.account.load().subscribe({
          next: (book) => this.watching.set(book.watchlist),
          error: () => undefined,
        });
        this.toast.show(
          this.lang.t('trFilled')
            .replace('{side}', order.side === 'BUY' ? this.lang.t('trBuy') : this.lang.t('trSell'))
            .replace('{qty}', formatQty(order.quantity))
            .replace('{symbol}', order.symbol)
            .replace('{price}', formatPrice(order.fillPrice, quote.precision)),
        );
      },
      error: (error: unknown) => {
        this.submitting.set(false);
        this.toast.show(errorMessage(error, this.lang.t('trOrderFail')), 'bad');
      },
    });
  }
}
